// maths.portll.net — "Your Data" question generators.
//
// The payoff loop: a kid collects real data on a Home Mission, and the app turns
// THAT data into questions ("New questions about YOUR data are waiting"). Generators
// are PURE — no Date, no Math.random. Answer-option order varies per question, but
// deterministically: the shuffle is seeded off the data itself, so the same dataset
// always yields the same pack (safe to render, cache, and print).
//
// Closed set: three beta dataset kinds → nine generator slugs (content
// `generatorHints` picks which ones run over a given dataset). New TASKS are content;
// only a new dataset KIND would ever need new engine code here.
//
//   categorical_tally → cat_most_least · cat_difference · cat_pictograph_read
//   trial_outcomes    → trial_observed_vs_expected · trial_longrun
//   numerical_list    → num_min_max_range · num_median · num_mode · num_outlier
//
// @typedef {{ id:string, prompt:string, options:string[], answerIndex:number, explanation:string }} Question

// ── deterministic helpers (pure) ─────────────────────────────────────────────
const round1 = (n) => Math.round(n * 10) / 10;

/** Format a number for display: integers plain, else one decimal. */
function fmt(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n);
  return String(round1(n));
}

/** Stable 32-bit hash of the dataset — the seed for all shuffles in a pack. */
function seedFromData(data) {
  let h = 2166136261;
  const s = JSON.stringify(data ?? {});
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Small deterministic PRNG (Lehmer / MINSTD). */
function makeRng(seed) {
  let s = (Math.abs(Math.trunc(seed)) % 2147483647) || 1;
  return () => {
    s = (s * 48271) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/** Deterministic Fisher–Yates. */
function shuffled(arr, seed) {
  const a = arr.slice();
  const rng = makeRng(seed);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Build a shuffled option list from a correct answer + candidate distractors.
 * Dedupes on string value, keeps at most `max` options, always includes `correct`.
 */
function buildOptions(correct, distractors, seed, max = 4) {
  const correctStr = String(correct);
  const seen = new Set([correctStr]);
  const pool = [correctStr];
  for (const d of distractors) {
    const ds = String(d);
    if (!seen.has(ds)) {
      seen.add(ds);
      pool.push(ds);
    }
    if (pool.length >= max) break;
  }
  const options = shuffled(pool, seed);
  return { options, answerIndex: options.indexOf(correctStr) };
}

/**
 * Numeric variant: candidates are numbers; formatted + deduped, then padded with
 * near-misses (correct ± 1, 2, …) so there are always ≥3 plausible distractors.
 */
function numOptions(correct, candidates, seed, max = 4) {
  const correctStr = fmt(correct);
  const seen = new Set([correctStr]);
  const distractors = [];
  const push = (n) => {
    if (typeof n !== "number" || !Number.isFinite(n)) return;
    const s = fmt(n);
    if (!seen.has(s)) {
      seen.add(s);
      distractors.push(s);
    }
  };
  for (const n of candidates) push(n);
  for (let delta = 1; distractors.length < 3 && delta < 50; delta++) {
    push(correct + delta);
    push(correct - delta);
  }
  return buildOptions(correctStr, distractors, seed, max);
}

const qFrom = ({ options, answerIndex }, prompt, explanation) => ({ prompt, options, answerIndex, explanation });

// ── shape adapters ───────────────────────────────────────────────────────────
function catSorted(data) {
  return (data?.categories || [])
    .filter((c) => c && typeof c.count === "number" && Number.isFinite(c.count))
    .map((c) => ({ label: String(c.label), count: c.count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function trialSorted(data) {
  const outs = (data?.outcomes || [])
    .filter((o) => o && typeof o.count === "number" && Number.isFinite(o.count))
    .map((o) => ({ label: String(o.label), count: o.count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const observed = outs.reduce((s, o) => s + o.count, 0);
  const trials = Number.isInteger(data?.trials) && data.trials > 0 ? data.trials : observed;
  return { outs, observed, trials };
}

function nums(data) {
  return (data?.values || []).filter((v) => typeof v === "number" && Number.isFinite(v));
}
const sortedNums = (data) => nums(data).slice().sort((a, b) => a - b);
function median(sorted) {
  const n = sorted.length;
  if (!n) return NaN;
  const mid = Math.floor(n / 2);
  return n % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}
const mean = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : NaN);

// ── categorical_tally generators ─────────────────────────────────────────────
function catMostLeast(data, seed) {
  const cats = catSorted(data);
  if (cats.length < 2) return [];
  const qs = [];
  // "most" — only when there's a clear winner (no tie at the top)
  if (cats[0].count > cats[1].count) {
    qs.push(
      qFrom(
        buildOptions(cats[0].label, cats.slice(1).map((c) => c.label), seed + 11),
        "Which one did you count the most of?",
        `You counted ${cats[0].count} ${cats[0].label} — more than any other. Great spotting!`
      )
    );
  }
  // "least" — only when there's a clear bottom
  const last = cats[cats.length - 1];
  const prev = cats[cats.length - 2];
  if (last.count < prev.count) {
    qs.push(
      qFrom(
        buildOptions(last.label, cats.slice(0, -1).map((c) => c.label), seed + 12),
        "Which one did you count the fewest of?",
        `${last.label} turned up just ${last.count} time${last.count === 1 ? "" : "s"} — the fewest of all.`
      )
    );
  }
  return qs;
}

function catDifference(data, seed) {
  const cats = catSorted(data);
  if (cats.length < 2) return [];
  const top = cats[0];
  const bot = cats[cats.length - 1];
  if (!(top.count > bot.count)) return []; // all equal → no difference to ask about
  if (cats[1].count === top.count) return []; // ambiguous "top"
  const diff = top.count - bot.count;
  const distractors = [top.count, bot.count, top.count + bot.count, diff + 1, diff - 1, cats[1].count];
  return [
    qFrom(
      numOptions(diff, distractors, seed + 13),
      `How many more ${top.label} than ${bot.label} did you count?`,
      `You had ${top.count} ${top.label} and ${bot.count} ${bot.label}, so ${top.count} − ${bot.count} = ${diff}.`
    ),
  ];
}

function catPictograph(data, seed) {
  const cats = catSorted(data);
  if (!cats.length) return [];
  const unit = data.unitLabel ? ` ${data.unitLabel}` : "";
  for (const cat of cats) {
    for (const k of [10, 5, 4, 3, 2]) {
      if (cat.count % k === 0) {
        const symbols = cat.count / k;
        if (symbols >= 2 && symbols <= 20) {
          const total = symbols * k; // == cat.count, read back from the pictograph
          const distractors = [symbols + k, symbols * (k + 1), cat.count + k, cat.count - k, symbols, k];
          return [
            qFrom(
              numOptions(total, distractors, seed + 14),
              `On a pictograph, each ⬤ stands for ${k}. You drew ${symbols} ⬤ for ${cat.label}. How many${unit || ""} is that?`,
              `${symbols} pictures × ${k} each = ${total}.`
            ),
          ];
        }
      }
    }
  }
  return [];
}

// ── trial_outcomes generators ────────────────────────────────────────────────
function trialObservedVsExpected(data, seed) {
  const { outs, observed, trials } = trialSorted(data);
  if (outs.length < 2 || observed <= 0) return [];
  const n = outs.length;
  const qs = [];

  // (a) expected count if every result is equally likely
  const expected = trials / n;
  const eRound = Math.round(expected);
  qs.push(
    qFrom(
      numOptions(eRound, [trials, n, eRound + 1, eRound - 1, eRound * 2], seed + 21),
      `You did ${trials} tries with ${n} equally-likely results. About how many of each would you expect?`,
      `${trials} shared evenly between ${n} results is about ${fmt(expected)} of each.`
    )
  );

  // (b) which result beat its even share (uses observed total, so it reflects reality)
  const expPer = observed / n;
  const byDev = outs
    .map((o) => ({ ...o, dev: o.count - expPer }))
    .sort((a, b) => b.dev - a.dev || a.label.localeCompare(b.label));
  if (byDev[0].dev > 0 && byDev[0].dev !== byDev[1].dev) {
    qs.push(
      qFrom(
        buildOptions(byDev[0].label, outs.map((o) => o.label), seed + 22),
        "If every result were equally likely, which one came up MORE often than you'd expect?",
        `${byDev[0].label} came up ${byDev[0].count} times — above the even share of about ${fmt(expPer)}.`
      )
    );
  }
  return qs;
}

function trialLongRun(data, seed) {
  const { outs, observed } = trialSorted(data);
  if (outs.length < 2 || observed <= 0) return [];
  const qs = [];

  // (a) best guess for next trial = the long-run most frequent outcome
  if (outs[0].count > outs[1].count) {
    qs.push(
      qFrom(
        buildOptions(outs[0].label, outs.map((o) => o.label), seed + 23),
        "Based on what happened so far, which result is most likely to come up next?",
        `${outs[0].label} happened most often (${outs[0].count} of ${observed}), so it's the best guess for next time.`
      )
    );
  }

  // (b) the top outcome's share, as a percentage
  const top = outs[0];
  const share = Math.round((100 * top.count) / observed);
  const pctCorrect = `${share}%`;
  const pdist = outs.slice(1).map((o) => `${Math.round((100 * o.count) / observed)}%`);
  pdist.push(`${Math.min(100, share + 10)}%`, `${Math.max(0, share - 10)}%`, `${100 - share}%`);
  qs.push(
    qFrom(
      buildOptions(pctCorrect, pdist, seed + 24),
      `About what share of your ${observed} tries were ${top.label}?`,
      `${top.count} out of ${observed} is about ${share}%.`
    )
  );
  return qs;
}

// ── numerical_list generators ────────────────────────────────────────────────
function numMinMaxRange(data, seed) {
  const s = sortedNums(data);
  if (s.length < 2) return [];
  const min = s[0];
  const max = s[s.length - 1];
  const m = mean(s);
  const unit = data.unitLabel ? ` (${data.unitLabel})` : "";
  const qs = [];

  qs.push(
    qFrom(
      numOptions(max, [min, s[s.length - 2], max - 1, max + 1, m], seed + 31),
      `What was the biggest number you measured${unit}?`,
      `The biggest was ${fmt(max)}.`
    )
  );
  qs.push(
    qFrom(
      numOptions(min, [max, s[1], min + 1, min - 1, m], seed + 32),
      `What was the smallest number you measured${unit}?`,
      `The smallest was ${fmt(min)}.`
    )
  );
  if (max > min) {
    const range = max - min;
    qs.push(
      qFrom(
        numOptions(range, [max, min, max + min, range + 1, range - 1], seed + 33),
        "What's the range — the biggest take away the smallest?",
        `${fmt(max)} − ${fmt(min)} = ${fmt(range)}.`
      )
    );
  }
  return qs;
}

function numMedian(data, seed) {
  const s = sortedNums(data);
  if (s.length < 3) return [];
  const med = median(s);
  const distractors = [mean(s), s[0], s[s.length - 1], med + 1, med - 1, s[Math.floor(s.length / 2)]];
  return [
    qFrom(
      numOptions(med, distractors, seed + 34),
      "Line your numbers up in order. What's the one right in the middle (the median)?",
      `Put in order, the middle value is ${fmt(med)}.`
    ),
  ];
}

function numMode(data, seed) {
  const arr = nums(data);
  if (arr.length < 3) return [];
  const freq = new Map();
  for (const v of arr) freq.set(v, (freq.get(v) || 0) + 1);
  let best = 0;
  for (const c of freq.values()) best = Math.max(best, c);
  const modes = [...freq.entries()].filter(([, c]) => c === best).map(([v]) => v);
  if (best < 2 || modes.length !== 1) return []; // no repeats, or a tie → skip
  const mode = modes[0];
  const s = sortedNums(data);
  const distractors = [median(s), mean(arr), s[0], s[s.length - 1], ...[...freq.keys()].filter((v) => v !== mode)];
  return [
    qFrom(
      numOptions(mode, distractors, seed + 35),
      "Which number came up the most times (the mode)?",
      `${fmt(mode)} showed up ${best} times — more than any other number.`
    ),
  ];
}

function numOutlier(data, seed) {
  const s = sortedNums(data);
  if (s.length < 4) return [];
  const med = median(s);
  const byDist = s.map((v) => ({ v, d: Math.abs(v - med) })).sort((a, b) => b.d - a.d);
  // need a single value that clearly stands apart from the pack
  if (!(byDist[0].d > 0) || byDist[0].d === byDist[1].d || byDist[0].d < byDist[1].d * 1.5) return [];
  const outlier = byDist[0].v;
  const cluster = byDist
    .slice()
    .sort((a, b) => a.d - b.d)
    .map((x) => x.v)
    .filter((v) => v !== outlier);
  return [
    qFrom(
      numOptions(outlier, cluster, seed + 36),
      "Most of your numbers sit close together. Which one is the odd one out?",
      `${fmt(outlier)} sits far from the rest — that's the odd one out.`
    ),
  ];
}

// ── registry + entry point ───────────────────────────────────────────────────
const GENERATORS = {
  cat_most_least: catMostLeast,
  cat_difference: catDifference,
  cat_pictograph_read: catPictograph,
  trial_observed_vs_expected: trialObservedVsExpected,
  trial_longrun: trialLongRun,
  num_min_max_range: numMinMaxRange,
  num_median: numMedian,
  num_mode: numMode,
  num_outlier: numOutlier,
};

const KIND_DEFAULT_SLUGS = {
  categorical_tally: ["cat_most_least", "cat_difference", "cat_pictograph_read"],
  trial_outcomes: ["trial_observed_vs_expected", "trial_longrun"],
  numerical_list: ["num_min_max_range", "num_median", "num_mode", "num_outlier"],
};

const KIND_PREFIX = {
  categorical_tally: "cat_",
  trial_outcomes: "trial_",
  numerical_list: "num_",
};

/**
 * Build a "Your Data" question pack from a dataset.
 * @param {'categorical_tally'|'trial_outcomes'|'numerical_list'} kind
 * @param {object} data  the dataset's `data` payload (see datasetShapes.js)
 * @param {string[]} [hints]  content generatorHints; empty/absent → all slugs for the kind
 * @returns {Question[]}
 */
export function generatePack(kind, data, hints) {
  const defaults = KIND_DEFAULT_SLUGS[kind];
  if (!defaults || !data) return [];
  const prefix = KIND_PREFIX[kind];

  let slugs = Array.isArray(hints)
    ? hints.filter((h) => typeof h === "string" && h.startsWith(prefix) && GENERATORS[h])
    : [];
  if (!slugs.length) slugs = defaults;
  slugs = [...new Set(slugs)]; // de-dupe, preserve order

  const seed = seedFromData(data);
  const out = [];
  let i = 0;
  for (const slug of slugs) {
    const gen = GENERATORS[slug];
    if (!gen) continue;
    let produced = [];
    try {
      produced = gen(data, seed) || [];
    } catch {
      produced = [];
    }
    for (const q of produced) {
      // guard: a real, answerable question with a valid answer index
      if (!q || !Array.isArray(q.options) || q.options.length < 2) continue;
      if (typeof q.answerIndex !== "number" || q.answerIndex < 0 || q.answerIndex >= q.options.length) continue;
      out.push({
        id: `${slug}-${i++}`,
        prompt: q.prompt,
        options: q.options,
        answerIndex: q.answerIndex,
        explanation: q.explanation,
      });
    }
  }
  return out;
}
